// src/sim/lighting/index.ts — P2 lighting module barrel + round-boot seeding hook.
//
// Invariants:
//   - initLighting(rng) must be called ONCE per round before any computeTorchFov() invocation.
//   - ROT.RNG is a module-level singleton (Pitfall 2); seeding here keeps determinism intact.
//
// Source: .planning/phases/02-fov-lighting/02-RESEARCH.md §Pitfall 2, §Pattern 1.

import * as ROT from 'rot-js';
import type { SimRng } from '../rng';

export { computeTorchFov } from './fov';
export {
  advanceMotionLights,
  MOTION_LIGHT_STAY_TICKS,
  MOTION_LIGHT_FADE_TICKS,
  type MotionLightEvent,
} from './lights';
export { lightLevelAt, type TorchLightSource } from './light-level';
export { allocBitset, setBit, getBit, clearBitset } from './bitset';
export * from './constants';

// TODO(P4 D-18): reserve entitiesFov: Record<string, FovSnapshot> for per-entity AI FOV at 10 Hz

/**
 * Seed ROT.RNG from the SimRng.motionLights sub-seed exactly once per round.
 *
 * Pitfall 2: ROT.RNG is a process-wide singleton. If any future lighting feature
 * calls ROT.RNG.getUniform() (e.g., flicker, jitter), it MUST be seeded from the
 * sim's seed tree first or determinism is a coin-flip across runs/workers.
 *
 * Called from createInitialWorldState (src/sim/world.ts) so ROT.RNG is in a known
 * state before tick(0) runs computeTorchFov for the first time.
 */
export function initLighting(rng: SimRng): void {
  // SimRng.motionLights() returns a [0,1) float; convert to a 32-bit signed int.
  const seed = (rng.motionLights() * 0xffffffff) | 0;
  ROT.RNG.setSeed(seed);
}
